import AppLayout from '../../components/dashboard/Layouts/AppLayout';
import { useState, useEffect, useRef } from 'react';
import RoleEnum from "../../components/enums/RoleEnum";
import ForceAuthentication from '../../components/auth/ForceAuthentication';
import DashboardCard from "../../components/dashboard/dashboard/DashboardCard";
import { Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from "../../hooks/auth";
import axios from "../../libs/axios";
import toast from 'react-hot-toast';
// Modularized team components
import TeamsList from "./teams/TeamsList";
import TeamMembersGrid from "./teams/TeamMembersGrid";
import TeamsStats from "./teams/TeamsStats";
import TeamActionButtons from "./teams/TeamActionButtons";
import TeamFeaturesGrid from "./teams/TeamFeaturesGrid";
import TeamInviteModal from "./teams/modals/TeamInviteModal";
import TeamLeaveModal from "./teams/modals/TeamLeaveModal";
import TeamDeleteModal from "./teams/modals/TeamDeleteModal";

const TeamsDashboardContent = ({ user }) => {
    const [teams, setTeams] = useState([]);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
    const [copied, setCopied] = useState(false);
    const [isLoadingMembers, setIsLoadingMembers] = useState(false);
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [isTeamsListCollapsed, setIsTeamsListCollapsed] = useState(false);
    const apiCallMadeRef = useRef(false);
    const { csrf } = useAuth();

    // Keyboard shortcut: Ctrl+B (Win/Linux) or Cmd+B (Mac) toggles teams list
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Check for Ctrl+B (Win/Linux) or Cmd+B (Mac)
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
                e.preventDefault();
                setIsTeamsListCollapsed(prev => !prev);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Main data fetching effect
    useEffect(() => {
        document.title = "Crafting AI Prompts Framework - Teams";
    }, [user]);

    // Handle team deletion
    const handleDeleteTeam = async () => {
        try {
            await csrf();
            await axios.delete(`/teams/${selectedTeam}`);
            const newTeams = teams.filter(t => t.id !== selectedTeam);
            setTeams(newTeams);
            let nextTeamId = null;
            if (newTeams.length === 0) {
                setSelectedTeam(null);
                setTeamMembers([]);
            } else {
                // Pick the next team in the list, or previous if at the end
                const idx = teams.findIndex(t => t.id === selectedTeam);
                let nextIdx = idx < newTeams.length ? idx : newTeams.length - 1;
                nextTeamId = newTeams[nextIdx]?.id || null;
                setSelectedTeam(nextTeamId);
                setTeamMembers([]);
                if (nextTeamId) {
                    // Load members for the new team
                    handleTeamSelect(nextTeamId);
                }
            }
            toast.success('Team deleted!');
        } catch (error) {
            toast.error('Failed to delete team. Please try again.');
        } finally {
            setShowDeleteModal(false);
            setDeleteConfirmInput('');
        }
    };

    // Handle toggling moderator status
    const handleMakeModerator = async (memberId, isModerator) => {
        let memberName = '';
        try {
            await csrf();
            // Find the member object to get the current role
            const member = teamMembers.find(m => m.id === memberId);
            if (!member) return;
            memberName = member.name;
            const newRole = isModerator ? 'moderator' : 'member';
            await axios.put(`/teams/${selectedTeam}/members/${memberId}`, {
                role: newRole,
                accepted: member.pivot.accepted
            });
            setTeamMembers(prev => prev.map(m =>
                m.id === memberId ? { ...m, pivot: { ...m.pivot, role: newRole } } : m
            ));
            toast.success(`Member: ${memberName} ${isModerator ? 'promoted to moderator' : 'demoted from moderator'}`);
        } catch (error) {
            toast.error('Failed to update moderator status. Please try again.');
        }
    };

    // In a real implementation, this would fetch teams from the backend
    const fetchData = async (isRefresh = false) => {
        if (!isRefresh && apiCallMadeRef.current) return;
        if (isRefresh) {
            setIsRefreshing(true);
        } else {
            setIsLoading(true);
        }

        try {
            setIsLoadingMembers(true);
            await csrf();
            const response = await axios.get('/teams');
            setTeams(response.data);
            // After teams are fetched, also refresh members for the selected team
            if (selectedTeam) {
                try {
                    const membersRes = await axios.get(`/teams/${selectedTeam}/members`);
                    setTeamMembers(membersRes.data);
                } catch (memberErr) {
                    setTeamMembers([]);
                }
            }
        } catch (error) {
            setError('Failed to load teams. Please try again.');
            setTeams([]);
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
            setIsLoadingMembers(false);
            apiCallMadeRef.current = true;
        }
    };
    
    // Handle team selection and load team members
    const handleTeamSelect = async (teamId) => {
        setIsLoadingMembers(true);
        setSelectedTeam(teamId);
        
        try {
            await csrf();
            const response = await axios.get(`/teams/${teamId}/members`);
            setTeamMembers(response.data);
        } catch (error) {
            toast.error('Failed to load team members. Please try again.');
            setTeamMembers([]);
        } finally {
            setIsLoadingMembers(false);
        }
    };
    
    // Handle removing a member from the team
    const handleRemoveMember = async (memberId) => {
        if (!memberId) return;
        if (!selectedTeam) return;
        const removedMember = teamMembers.find(m => m.id === memberId);

        // Allow users to remove themselves regardless of role
        const isSelfRemove = memberId === user.id;
        try {
            await csrf();
            await axios.delete(`/teams/${selectedTeam}/members/${memberId}`);
            setTeamMembers(prev => prev.filter(member => member.id !== memberId));
            setTeams(prev => prev.map(team => {
                if (team.id === selectedTeam) {
                    let newActionRequired = team.action_required;
                    if (removedMember && removedMember.requested) {
                        newActionRequired = Math.max(0, team.action_required - 1);
                    }
                    return { ...team, members: team.members - 1, action_required: newActionRequired };
                }
                return team;
            }));
            toast.success(`Member: ${removedMember?.name || ''} deleted`);
            if (isSelfRemove) {
                setSelectedTeam(null);
                setTeamMembers([]);
            }
        } catch (error) {
            if (isSelfRemove && error?.response?.status === 403 && error?.response?.data?.message?.includes('Only owners or moderators can remove members.')) {
                // Ignore this error, allow self-removal UX
                setSelectedTeam(null);
                setTeamMembers([]);
                toast.success('You have left the team.');
            } else if (error?.response?.status === 403 && error?.response?.data?.message?.includes('Owners cannot be removed')) {
                toast.error('You cannot remove an owner from the team. Please delete the team instead.');
            } else {
                toast.error('Failed to remove team member. Please try again.');
            }
        }
    };

    // Handle leaving the team for current user
    const handleLeaveTeam = async () => {
        setShowLeaveModal(false);
        try {
            await csrf();
            await axios.delete(`/teams/${selectedTeam}/members/${user.id}`);
            setTeams(prev => prev.filter(team => team.id !== selectedTeam)); // Remove left team from UI
            setSelectedTeam(null);
            setTeamMembers([]);
            toast.success('You have left the team.');
        } catch (error) {
            toast.error('Failed to leave the team. Please try again.');
        }
    };

    // Update team settings (e.g. name)
    const handleUpdateTeam = async (updatedTeam) => {
        try {
            await csrf();
            const response = await axios.put(`/teams/${updatedTeam.id}`, {
                name: updatedTeam.name,
                inviteLinkEnabled: updatedTeam.inviteLinkEnabled,
                url_key: updatedTeam.url_key
            });
            setTeams(prev => prev.map(team => team.id === updatedTeam.id ? { ...team, ...response.data } : team));
            toast.success('Team settings updated');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to update team settings. Please try again.');
        }
    };


    // Accept a pending member (set accepted to true in backend)
    const handleAcceptMember = async (memberId) => {
        let memberName = '';
        try {
            await csrf();
            await axios.post(`/teams/${selectedTeam}/members/${memberId}/accept`);
            setTeamMembers(prev => prev.map(member => {
                if (member.id === memberId) {
                    memberName = member.name;
                    return {
                        ...member,
                        pivot: {
                            ...member.pivot,
                            accepted: 1,
                            role: member.pivot.role === 'pending' ? 'member' : member.pivot.role
                        }
                    };
                }
                return member;
            }));
            setTeams(prevTeams => prevTeams.map(team => {
                if (team.id === selectedTeam) {
                    return {
                        ...team,
                        action_required: Math.max(0, (team.action_required || 1) - 1)
                    };
                }
                return team;
            }));
            toast.success(`Member: ${memberName} accepted`);
        } catch (error) {
            toast.error('Failed to accept member. Please try again.');
        }
    };

    // Decline a pending member (remove from backend and list)
    const handleDeclineMember = async (memberId) => {
        const declinedMember = teamMembers.find(m => m.id === memberId);
        try {
            await csrf();
            await axios.post(`/teams/${selectedTeam}/members/${memberId}/decline`);
            setTeamMembers(prev => prev.filter(member => member.id !== memberId));
            setTeams(prevTeams => prevTeams.map(team => {
                if (team.id === selectedTeam) {
                    return {
                        ...team,
                        action_required: Math.max(0, (team.action_required || 1) - 1)
                    };
                }
                return team;
            }));
            toast.success(`Member: ${declinedMember?.name || ''} declined`);
        } catch (error) {
            toast.error('Failed to decline member. Please try again.');
        }
    };

    // Load data on component mount and auto-select first team if available
    useEffect(() => {
        fetchData();
    }, []);

    // Auto-select first team if available and none selected
    useEffect(() => {
        if (!isLoading && teams.length > 0 && !selectedTeam) {
            handleTeamSelect(teams[0].id);
        }
    }, [isLoading, teams, selectedTeam]);
    
    // Only allow DefaultUser and higher
    if (!user || user.role < RoleEnum.DefaultUser) {
        return (
            <ForceAuthentication />
        );
    }

    // Stats calculations
    const totalTeams = teams.length;
    const totalUsersInTeams = teams.reduce((sum, t) => sum + (typeof t.members === 'number' && !isNaN(t.members) ? t.members : 0), 0);

    // Create a new team and send to backend
    const handleCreateTeam = async (teamName) => {
        try {
            await csrf();
            const response = await axios.post('/teams', { name: teamName });
            toast.success('Team created!');
            // Refetch teams to ensure the new team has all summary fields
            await fetchData(true);
            // Find the new team id from the latest teams
            const latestTeams = await axios.get('/teams');
            const newTeamId = latestTeams.data.find(t => t.name === teamName)?.id;
            return newTeamId;
        } catch (error) {
            toast.error('Failed to create team. Please try again.');
            return null;
        }
    };

    // Get current user's role in the selected team
    const currentUserRole = teams.find(t => t.id === selectedTeam)?.current_user_role || '';

    return (
        <AppLayout
            header={
                <h2 className="font-semibold text-xl text-gray-800 dark:text-white leading-tight">
                    Teams Dashboard
                </h2>
            }>
            <div className="py-8 mx-3 lg:mx-0">
                <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
                    {/* First row: Title/description on left, stats on right */}
                    <div className="grid grid-cols-1 lg:grid-cols-3">
                        <DashboardCard className="md:col-span-2 overflow-hidden mb-4 lg:mr-4">
                            <div className="flex items-center gap-6 rounded-xl">
                                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#468ef9] to-[#0c66ee] dark:from-[#0c66ee] dark:to-[#468ef9] flex items-center justify-center">
                                    <span className="text-2xl font-bold text-white dark:text-black">
                                        {user?.name?.[0]?.toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                                        Team Collaboration Center
                                    </h1>
                                    <p className="text-gray-600 dark:text-gray-300 mt-1">Create teams and collaborate with other Prompt Engineers.</p>
                                    <p className="text-gray-600 dark:text-gray-300 mt-1">Please look below for the roadmap. This page is currently under construction.</p>
                                </div>
                            </div>
                        </DashboardCard>
                        
                        <DashboardCard className="overflow-hidden mb-4">
                            <TeamsStats
                                totalTeams={teams.length}
                                totalUsersInTeams={teams.reduce((sum, t) => sum + (t.members || 0), 0)}
                                totalActionsRequired={teams.reduce((sum, t) => sum + (t.action_required || 0), 0)}
                                isRefreshing={isRefreshing}
                                fetchData={fetchData}
                            />
                        </DashboardCard>
                    </div>

                    {/* Second row: Teams list on left, Team members on right */}
                    <div className="flex flex-col lg:flex-row gap-4 lg:mb-4">
                        <div className={`transition-all duration-300 ease-in-out ${isTeamsListCollapsed ? 'lg:w-16' : 'lg:w-1/4 w-full'}`}>
                            <DashboardCard className="h-full overflow-hidden lg:mb-4 flex flex-col">
                                {isTeamsListCollapsed ? (
                                    <div className="flex flex-col items-center justify-center h-full space-y-6">
                                        <button 
                                            onClick={() => setIsTeamsListCollapsed(!isTeamsListCollapsed)}
                                            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-all duration-200"
                                            title="Expand teams list"
                                            aria-label="Expand teams list"
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                        <div className="md:block hidden vertical-text text-md font-medium text-gray-500 dark:text-gray-400 pt-2" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}>
                                            <strong>Your Teams</strong>
                                        </div>
                                        <div className="md:hidden text-md font-medium text-gray-500 dark:text-gray-400 pt-2">
                                            <strong>Your Teams</strong>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-center justify-between mb-4">
                                            <h2 className="text-xl font-semibold mb-4 dark:text-white">Your Teams</h2>
                                            <button 
                                                onClick={() => setIsTeamsListCollapsed(!isTeamsListCollapsed)}
                                                className="p-2 -mt-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-all duration-200 ml-auto"
                                                title="Collapse teams list"
                                                aria-label="Collapse teams list"
                                            >
                                                <ChevronLeft size={18} />
                                            </button>
                                        </div>
                                        <TeamsList
                                            loading={isLoading}
                                            teams={teams}
                                            selectedTeam={selectedTeam}
                                            onSelect={handleTeamSelect}
                                            user={user}
                                            currentUserRole={currentUserRole}
                                            onCreateTeam={handleCreateTeam}
                                            onUpdateTeam={handleUpdateTeam}
                                        />
                                    </>
                                )}
                            </DashboardCard>
                        </div>
                        <div className="flex-1">
                            <DashboardCard className="h-full overflow-hidden mb-4">
                            {selectedTeam ? (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex-1">
                                            <h2 className="text-xl font-semibold dark:text-white">
                                                {teams.find(t => t.id === selectedTeam)?.name} Members
                                            </h2>
                                        </div>
                                        <TeamActionButtons
                                            team={teams.find(t => t.id === selectedTeam)}
                                            onDelete={() => setShowDeleteModal(true)}
                                            onInvite={() => setShowInviteModal(true)}
                                            onLeave={() => setShowLeaveModal(true)}
                                            showDelete={teams.find(t => t.id === selectedTeam)?.current_user_role === 'owner'}
                                            showInvite={['owner', 'moderator'].includes(teams.find(t => t.id === selectedTeam)?.current_user_role)}
                                            showLeave={teams.find(t => t.id === selectedTeam)?.current_user_role !== 'owner'}
                                            inviteDisabled={!teams.find(t => t.id === selectedTeam)?.inviteLinkEnabled}
                                        />
                                    </div>
                                    <TeamMembersGrid
                                        teamMembers={teamMembers}
                                        teamsLoading={isLoading}
                                        isLoadingMembers={isLoadingMembers}
                                        onRemove={handleRemoveMember}
                                        onMakeModerator={handleMakeModerator}
                                        currentUserId={user.id}
                                        onAccept={handleAcceptMember}
                                        onDecline={handleDeclineMember}
                                        inviteLinkEnabled={teams.find(t => t.id === selectedTeam)?.inviteLinkEnabled !== false}
                                        currentUserRole={teams.find(t => t.id === selectedTeam)?.current_user_role}
                                    />
                                </div>
                            ) : (
                                <>
                                {isLoading || isLoadingMembers ? (
                                                <>
                                                {/* Title Skeleton */}
                                                <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                                    <div className="flex flex-col sm:flex-row gap-2 w-full">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="h-10 w-full bg-gray-200 dark:bg-gray-800 rounded-xl mb-2 animate-pulse"></div>
                                                        </div>
                                                        <div className="h-10 w-32 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse"></div>
                                                    </div>
                                                </div>
                                                {/* Search bar skeleton */}
                                                <div className="mb-4">
                                                    <div className="h-10 w-full bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse"></div>
                                                </div>
                                                {/* Grid of loading cards skeletons */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                    {[...Array(3)].map((_, index) => (
                                                        <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm animate-pulse">
                                                            <div className="rounded-t-lg px-3 py-2.5 mb-3 bg-gray-100 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                                                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                                                </div>
                                                            </div>
                                                            <div className="p-3">
                                                                <div className="flex items-center justify-between gap-2 mb-4">
                                                                    <div className="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                                                                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                                                </div>
                                                                <div className="flex items-center justify-between gap-2 mb-3">
                                                                    <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                                                    <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                                                </div>
                                                            </div>
                                                            <div className="px-3 py-2 bg-gray-100 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 rounded-b-lg">
                                                                <div className="flex justify-end">
                                                                    <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12">
                                        <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
                                        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2">Select a Team</h3>
                                        <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
                                            Choose a team from the list on the left to view its members
                                        </p>
                                    </div>
                                )}
                                </>
                            )}
                            </DashboardCard>
                        </div>
                    </div>

                    {/* Third row: Teams features */}
                    <div className="grid grid-cols-1 gap-4 mb-4 dark:bg-gray-800/50">
                        <DashboardCard className="overflow-hidden bg-gradient-to-br from-white to-gray-50/50 dark:from-gray-800 dark:to-gray-800/95 shadow-lg hover:shadow-xl transition-all duration-300">
                            <TeamFeaturesGrid />
                        </DashboardCard>
                    </div>

                    <TeamDeleteModal
                        isOpen={showDeleteModal}
                        onClose={() => { setShowDeleteModal(false); setDeleteConfirmInput(''); }}
                        teamName={teams.find(t => t.id === selectedTeam)?.name || ''}
                        confirmInput={deleteConfirmInput}
                        setConfirmInput={setDeleteConfirmInput}
                        onDelete={handleDeleteTeam}
                        />
                    
                    <TeamLeaveModal
                        isOpen={showLeaveModal}
                        onClose={() => setShowLeaveModal(false)}
                        onLeave={handleLeaveTeam}
                    />

                    <TeamInviteModal
                        isOpen={showInviteModal}
                        onClose={() => setShowInviteModal(false)}
                        inviteUrl={`${window.location.origin}/dashboard/teams/${teams.find(t => t.id === selectedTeam)?.url_key || ''}`}
                        copied={copied}
                        setCopied={setCopied}
                        />
                </div>
            </div>
        </AppLayout>
    );
};

const TeamsDashboard = () => {
    return (
        <ForceAuthentication
            requireRole={1}
            accessDeniedTitle="Access Denied"
            accessDeniedMessage="You don't have permission to access this page."
            redirectIfNotAuthenticated="/dashboard"
        >
            {({ user }) => (
                <TeamsDashboardContent user={user} />
            )}
        </ForceAuthentication>
    );
};

export default TeamsDashboard;
